/**
 * ЭФФЕКТЫ ПОДЛОЖЕК — у каждой подложки поля своя небольшая анимация, родная
 * ее пейзажу: порывы ветра над лугом, поземка на снегу, блики соли, огоньки над
 * болотом (см. scenes/FieldBiomeEffects). Вместе со стаями птиц, облаками и
 * орлами (configs/fieldAmbience) это антураж поля: мы смотрим на него с большой
 * высоты, как на живую карту мира.
 *
 * Эффекты целиком визуальные: в состояние партии не попадают, у каждого клиента
 * свои, на правила и записи матчей не влияют.
 *
 * Здесь — общее для всех эффектов: слои, текстуры, потолок числа спрайтов.
 * Какой подложке какой эффект — в configs/biomeEffects/index.js, числа каждого
 * эффекта — в его файле рядом, геометрия — в utils/effects/biome.
 */

/**
 * Слои эффектов среди слоев поля (см. LAYER_DEPTH в HexGridRenderer и
 * FIELD_AMBIENCE_DEPTH). У каждого слоя свой режим наложения: смена режима
 * между соседними спрайтами рвет пачку отрисовки, поэтому светящиеся спрайты
 * (ADD) лежат на своей глубине, а не вперемешку с обычными.
 *
 *  - GROUND / GROUND_GLOW — на самой земле: между картинкой пейзажа (-2) и ее
 *    затемнением у краев (-1). Под дымкой пустых ячеек и под жетонами: то, что
 *    лежит на земле, жетон закрывает, а край пейзажа гасит сам (isUnderEdgeFade:
 *    контроллер такие слои у края не гасит — иначе они гасли бы дважды);
 *  - CELL / CELL_GLOW — над дымкой пустых ячеек (0.9), но под жетонами (0.99 и
 *    выше): для того, что на светлой земле под дымкой не разглядеть;
 *  - LOW_AIR / LOW_AIR_GLOW — низко над полем: над жетонами строений и гор
 *    (до 1.3), но под подсветкой ячеек (1.5), отрядами и их значками. Здесь
 *    стелется поземка и идет основная масса вьюги;
 *  - AIR / AIR_GLOW — высоко: над отрядами и их значками (до 4.0) и искрами
 *    попаданий (4.6), под тенями облаков (4.7), птицами и облаками. Только для
 *    редкого и мелкого — оно пролетает над отрядами. Два исключения, оба — по
 *    решению владельца игры: пылевой вихрь красной котловины (столб пыли идет
 *    над всем полем) и редкий бледный снег вьюги поверх отрядов.
 */
export const FIELD_BIOME_LAYERS = Object.freeze({
  GROUND: Object.freeze({depth: -1.5, isGlow: false, isUnderEdgeFade: true}),
  GROUND_GLOW: Object.freeze({depth: -1.4, isGlow: true, isUnderEdgeFade: true}),
  CELL: Object.freeze({depth: 0.94, isGlow: false, isUnderEdgeFade: false}),
  CELL_GLOW: Object.freeze({depth: 0.95, isGlow: true, isUnderEdgeFade: false}),
  LOW_AIR: Object.freeze({depth: 1.4, isGlow: false, isUnderEdgeFade: false}),
  LOW_AIR_GLOW: Object.freeze({depth: 1.41, isGlow: true, isUnderEdgeFade: false}),
  AIR: Object.freeze({depth: 4.62, isGlow: false, isUnderEdgeFade: false}),
  AIR_GLOW: Object.freeze({depth: 4.63, isGlow: true, isUnderEdgeFade: false}),
});

/**
 * Наземные слои у края картинки гасит ее собственное затемнение — так же, как
 * нарисованное. Контроллеру остается только не выпустить частицу за край, на
 * черное поле: в этой узкой полосе у края (доли стороны картинки) частица
 * наземного слоя сходит на нет. Ширина полосы — под самые крупные частицы
 * (клуб в 0.08 стороны у внутренней кромки полосы еще не торчит за картинку).
 */
export const FIELD_BIOME_GROUND_EDGE_GUARD = Object.freeze({fromShare: 0.02, toShare: 0.06});

/**
 * Порядок внутри слоя: у частицы может быть номер (sublayer, от 0 до count - 1) —
 * частица с большим номером рисуется поверх (ореол блика — 0, сам блик — 1).
 * depthStep — шаг глубины между соседними номерами: все номера слоя умещаются
 * под следующим слоем поля.
 */
export const FIELD_BIOME_SUBLAYERS = Object.freeze({count: 4, depthStep: 0.002});

/** Имена слоев — для частиц в построителях эффектов */
export const FIELD_BIOME_LAYER_KEYS = Object.freeze(
  Object.fromEntries(Object.keys(FIELD_BIOME_LAYERS).map((key) => [key, key]))
);

/**
 * Текстуры эффектов: картинок в public нет, пиксели считает код (см.
 * utils/effects/fieldBiomeTextures). Почти все — белые, форма в прозрачности:
 * цвет частице дает краска спрайта. Исключения — зимние: в штрихе снежной
 * ленты нарисована его тень, а в рое снежинок часть хлопьев — серо-голубая.
 */
export const FIELD_BIOME_TEXTURES = Object.freeze({
  // Мягкое круглое пятно: свечение, блик, пылинка
  DOT: 'biome_dot',
  // Вытянутый мягкий штрих с сужающимися концами: струя снега, песка, ветра
  STREAK: 'biome_streak',
  // Листок: заостренный овал
  LEAF: 'biome_leaf',
  // Широкое мягкое кольцо: свечение по кругу
  RING: 'biome_ring',
  // Тонкое кольцо: круг на воде
  RIPPLE: 'biome_ripple',
  // Четкая четырехлучевая звезда: блик кристалла
  STAR: 'biome_star',
  // Мягкая звезда с тонкими лучами и ярким ядром: искра, солнечный блик
  SPARK: 'biome_spark',
  // Мягкий клуб с неровным краем: пар, дым, пыль
  PUFF: 'biome_puff',
  // Неровная чешуйка: хлопья пепла
  FLAKE: 'biome_flake',
  // Силуэт воронки смерча: узкое основание слева, к правому краю кадра
  // расходится и чуть изгибается — тень пылевого столба на земле
  FUNNEL: 'biome_funnel',
  // Штрих снежной ленты: тонкий белый гребень и серо-голубая тень под ним —
  // на белом снегу ленту выдает тень, на темном жетоне — гребень. Тень — с
  // нижней стороны кадра: штрих кладут, не переворачивая вверх ногами
  SNOW_STREAK: 'biome_snow_streak',
  // Рой снежинок: россыпь мелких округлых хлопьев — белых и светлых
  // серо-голубых. К краям кадра хлопья бледнеют: рои кладутся внахлест без
  // видимых границ
  FLURRY: 'biome_flurry',
  // Одна снежинка: белый круглый хлопок с плотной серединой и мягким краем —
  // такой же, как хлопья роя. Из них вьюга собирает завихрения: каждая
  // снежинка летит сама по себе
  SNOWFLAKE: 'biome_snowflake',
  // Прядь пыли: четыре тонких волоска бок о бок и легкая дымка между ними —
  // кусок шелковистой вуали соляной пыли. К концам кадра тает, не сужаясь
  TRESS: 'biome_tress',
  // Плоская линза с острыми концами: середина плотная, кромка мягкая —
  // «вода» миража над соляной коркой
  LENS: 'biome_lens',
  // Отрезок светящегося обода, выгнутый дугой: поперек — мягкий гребень, вдоль
  // плавно сходит на нет к обоим концам. Отрезки, уложенные по кругу внахлест
  // с шагом в половину длины, сливаются в ровное кольцо: из них собирают обод,
  // который загорается не целиком, а по частям. Концы дуги опущены к нижней
  // стороне кадра: ею отрезок кладут к центру кольца
  RIM_SEGMENT: 'biome_rim_segment',
  // Плотная круглая капля с ровным краем: сгусток магмы. В отличие от мягкого
  // пятна (DOT) — тело, а не свечение: остывший сгусток ею рисуют темным
  CLOT: 'biome_clot',
  // Прядь травяной волны: десятки тонких коротких стеблей вдоль оси Y кадра и
  // слабый мягкий отсвет под ними — кусок светлой полосы травы, которую
  // положил ветер. Верх кадра — передняя кромка волны: стебли начинаются у
  // нее четко и к нижней стороне кадра тают. Вдоль оси X прядь сходит на нет
  // плавно: пряди, уложенные внахлест с шагом в половину длины, сливаются в
  // ровную полосу — из них собирают выгнутый гребень волны. Кадр кладут верхом
  // по ветру, не переворачивая
  SWATH: 'biome_swath',
});

/**
 * Картинка грифа костяной подложки: вид сверху, голова вправо, крылья по оси
 * Y. Арт владельца игры, подготовленный скриптом
 * developer_artifacts/scripts/generateFieldAmbienceBirds.cjs (исходник — в
 * developer_artifacts/art_sources/field_ambience/birds): путь и размеры
 * вписывает он, как и загрузку картинки в GameScene.preload(). wingspan —
 * размах крыльев в точках картинки. Текстура эффектов не из кода: не
 * загрузилась — грифов на костяной подложке нет.
 */
export const FIELD_BIOME_VULTURE_IMAGE = Object.freeze({
  key: 'biome_vulture',
  path: '/img/effects/other/vulture.fb100db9.png',
  width: 59,
  height: 161,
  wingspan: 160,
});

/**
 * Картинки эффектов, которые грузит сама сцена (GameScene.preload), а не
 * заводит код: арт владельца игры
 */
export const FIELD_BIOME_IMAGES = Object.freeze([FIELD_BIOME_VULTURE_IMAGE]);

// Больше стольких спрайтов эффект подложки разом не держит: новая группа
// частиц, которой не хватает места, пропускает свою очередь. Число — под зиму,
// самую многолюдную из подложек: поземка (до 256 спрайтов) и самый густой заряд
// вьюги (до 283, из них 134 — отдельные снежинки завихрений) идут разом
export const FIELD_BIOME_MAX_SPRITES = 556;

// Слабее этого частица не рисуется вовсе
export const FIELD_BIOME_MIN_VISIBLE_ALPHA = 0.004;
